// hypercomb-shared/core/lineage.ts
// synchronize is dispatched only by the processor — lineage fires 'change' on itself

import { EffectBus } from '@hypercomb/core'
import type { Navigation } from './navigation'
import type { Store } from './store'

// global get/register/list available via ioc.web.ts

export class Lineage extends EventTarget {

  // -------------------------------------------------
  // dependencies
  // -------------------------------------------------

  private get store(): Store { return get('@hypercomb.social/Store') as Store }
  private get navigation(): Navigation { return get('@hypercomb.social/Navigation') as Navigation }

  // -------------------------------------------------
  // explorer path (domain-relative)
  // -------------------------------------------------

  private explorerPath: string[] = []
  public explorerSegments = (): readonly string[] => this.explorerPath

  public explorerEnter = (name: string): void => {
    const seg = (name ?? '').trim()
    if (!seg || seg === '.' || seg === '..') return

    // perf trail: navigation T0 (no-op on shells without __hcNav)
    ;(window as unknown as { __hcNav?: (l: string, e?: string) => void }).__hcNav?.('nav:start', seg)

    // do not normalize explorer names
    this.explorerPath = [...this.explorerPath, seg]
    this.invalidate()

    // explorer drives navigation (best effort)
    try {
      this.navigation.goRaw(this.explorerPath)
    } catch {
      // fallback: still notify followers even if navigation isn't ready
      this.dispatchNavigateFallback()
    }
  }

  public explorerUp = (): void => {
    if (this.explorerPath.length === 0) return
    this.explorerPath = this.explorerPath.slice(0, -1)
    this.invalidate()

    // explorer drives navigation (best effort)
    try {
      this.navigation.goRaw(this.explorerPath)
    } catch {
      this.dispatchNavigateFallback()
    }
  }

  // keeps old name so you don't have to refactor callers
  // this now means "show domain root"
  public showDomainRoot = (): void => {
    this.explorerPath = []
    this.invalidate()

    // explorer drives navigation (best effort)
    try {
      this.navigation.goRaw([])
    } catch {
      this.dispatchNavigateFallback()
    }
  }

  public explorerLabel = (): string => {
    return '/' + this.explorerPath.join('/')
  }

  // -------------------------------------------------
  // sigbag + current layer — the canonical layer-as-truth accessors.
  //
  // The navigation primitive IS the sigbag — "where the user is" =
  // "which __history__/<sig>/ bag they're addressing". Every reader
  // that wants "what tiles exist here?" or "what's this location's
  // state?" should go through these two methods. There is ONE source
  // of truth for sigbag computation (HistoryService.sign), and ONE
  // place we cache the resolution per navigation step.
  //
  // Invalidation: explorerPath change → invalidate() → fsRevision++
  //   → cache entries that don't match the new revision are stale.
  //   Both methods short-circuit on cached revision match.
  // -------------------------------------------------

  #cachedSig: { revision: number; sig: string } | null = null
  #pendingSig: { revision: number; promise: Promise<string> } | null = null
  #cachedLayer: { revision: number; layer: unknown } | null = null
  #pendingLayer: { revision: number; promise: Promise<unknown> } | null = null

  /** The sigbag for the current explorerPath. Delegates to
   *  HistoryService.sign (the single source of truth) and memoizes per
   *  fsRevision. Returns '' if HistoryService isn't registered yet. */
  public currentSig = async (): Promise<string> => {
    const revision = this.#fsRevision
    if (this.#cachedSig?.revision === revision) return this.#cachedSig.sig
    if (this.#pendingSig?.revision === revision) return this.#pendingSig.promise

    const promise = (async (): Promise<string> => {
      const history = get('@diamondcoreprocessor.com/HistoryService') as
        { sign?: (l: { explorerSegments?: () => readonly string[] }) => Promise<string> } | undefined
      if (!history?.sign) return ''
      const sig = await history.sign(this)
      if (this.#fsRevision === revision) {
        this.#cachedSig = { revision, sig }
      }
      return sig
    })()

    this.#pendingSig = { revision, promise }
    promise.finally(() => {
      if (this.#pendingSig?.promise === promise) this.#pendingSig = null
    })
    return promise
  }

  /** The current layer for the user's lineage location — the
   *  authoritative "what's here" view. Resolved as
   *  HistoryService.currentLayerAt(currentSig()). Cached per
   *  fsRevision so a render that asks for the layer multiple times
   *  pays one resolve. Returns null if HistoryService isn't ready
   *  or there's no committed layer at the location yet. */
  public currentLayer = async (): Promise<unknown> => {
    const revision = this.#fsRevision
    if (this.#cachedLayer?.revision === revision) return this.#cachedLayer.layer
    if (this.#pendingLayer?.revision === revision) return this.#pendingLayer.promise

    const promise = (async (): Promise<unknown> => {
      const sig = await this.currentSig()
      if (!sig) return null
      const history = get('@diamondcoreprocessor.com/HistoryService') as
        { currentLayerAt?: (s: string) => Promise<unknown> } | undefined
      if (!history?.currentLayerAt) return null
      const layer = await history.currentLayerAt(sig)
      if (this.#fsRevision === revision) {
        this.#cachedLayer = { revision, layer }
      }
      return layer
    })()

    this.#pendingLayer = { revision, promise }
    promise.finally(() => {
      if (this.#pendingLayer?.promise === promise) this.#pendingLayer = null
    })
    return promise
  }

  // Per-revision memoization. invalidate() bumps #fsRevision on any FS change,
  // so the cache is auto-invalidated. In-flight dedup is keyed on revision so
  // a stale walk that resolves after a new invalidate() can't poison the cache.
  #cachedExplorerDir: { revision: number; dir: FileSystemDirectoryHandle | null } | null = null
  #pendingExplorerDir: { revision: number; promise: Promise<FileSystemDirectoryHandle | null> } | null = null

  public explorerDir = async (): Promise<FileSystemDirectoryHandle | null> => {
    const revision = this.#fsRevision

    if (this.#cachedExplorerDir?.revision === revision) {
      return this.#cachedExplorerDir.dir
    }

    if (this.#pendingExplorerDir?.revision === revision) {
      return this.#pendingExplorerDir.promise
    }

    const promise = (async (): Promise<FileSystemDirectoryHandle | null> => {
      let dir: FileSystemDirectoryHandle | null = null
      try {
        dir = await this.tryResolveFrom(this.store.hypercombRoot, this.explorerPath)
      } catch {
        dir = null
      }
      if (this.#fsRevision === revision) {
        this.#cachedExplorerDir = { revision, dir }
      }
      return dir
    })()

    this.#pendingExplorerDir = { revision, promise }
    promise.finally(() => {
      if (this.#pendingExplorerDir?.promise === promise) {
        this.#pendingExplorerDir = null
      }
    })

    return promise
  }

  // -------------------------------------------------
  // status
  // -------------------------------------------------

  #ready = false
  #materialized = true
  #missing: readonly string[] = []
  #fsRevision = 0

  public get ready(): boolean { return this.#ready }
  public get materialized(): boolean { return this.#materialized }
  public get missing(): readonly string[] { return this.#missing }

  public changed = (): number => this.#fsRevision

  // -------------------------------------------------
  // lifecycle
  // -------------------------------------------------

  public constructor() {
    super()
    // follow url changes (programmatic + back/forward)
    window.addEventListener('navigate', this.followLocation)
    window.addEventListener('popstate', this.followLocation)

    // OPFS mutations — bump fsRevision so per-revision caches
    // (listCellFolders, branchSet, explorerDir) see fresh disk state.
    // `fs:changed` is the bulk-emit signal: workers fire it BEFORE
    // committing layer state so renders triggered by the cascade
    // (cursor.onNewLayer) see post-mutation OPFS without triggering
    // additional per-cell layer commits.
    EffectBus.on('fs:changed', this.invalidate)
    EffectBus.on('cell:added', this.invalidate)
    EffectBus.on('cell:removed', this.invalidate)

    // best-effort initial sync (safe if nav/store aren't ready yet)
    this.followLocation()

    this.#ready = true
    this.dispatchEvent(new CustomEvent('change'))
  }

  public initialize = async (): Promise<void> => {
    this.followLocation()
    this.#ready = true
    this.dispatchEvent(new CustomEvent('change'))
  }

  // -------------------------------------------------
  // domain resolution (used by navigation/search)
  // -------------------------------------------------

  public tryResolve = async (
    segments: readonly string[],
    start?: FileSystemDirectoryHandle
  ): Promise<FileSystemDirectoryHandle | null> => {
    if (!start) return null
    return await this.tryResolveFrom(start, segments)
  }

  private readonly tryResolveFrom = async (
    start: FileSystemDirectoryHandle,
    segments: readonly string[]
  ): Promise<FileSystemDirectoryHandle | null> => {

    let dir = start

    for (let i = 0; i < segments.length; i++) {
      const seg = (segments[i] ?? '').trim()
      if (!seg) continue

      try {
        dir = await dir.getDirectoryHandle(seg, { create: false })
      } catch {
        const wasMaterialized = this.#materialized
        const hadMissing = this.#missing.length > 0
        this.#materialized = false
        this.#missing = segments.slice(i)
        if (wasMaterialized || !hadMissing) this.dispatchEvent(new CustomEvent('change'))
        return null
      }
    }

    if (!this.#materialized || this.#missing.length > 0) {
      this.#materialized = true
      this.#missing = []
      this.dispatchEvent(new CustomEvent('change'))
    }
    return dir
  }

  public addMarker = async (_segments: readonly string[], _signature: string): Promise<void> => {
    // no-op: directory-based markers removed
  }

  // -------------------------------------------------
  // internal
  // -------------------------------------------------

  private readonly invalidate = (): void => {
    this.#fsRevision = this.#fsRevision + 1
    this.dispatchEvent(new CustomEvent('change'))
  }

  private readonly followLocation = (): void => {
    try {
      // explorer path must stay lossless; use raw decoded URL segments
      const next = this.navigation.segmentsRaw()

      // do not spam invalidations if nothing changed
      if (this.sameSegments(this.explorerPath, next)) return

      this.explorerPath = next
      this.invalidate()
    } catch {
      // ignore until nav is ready
    }
  }

  private readonly sameSegments = (a: readonly string[], b: readonly string[]): boolean => {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
      if ((a[i] ?? '') !== (b[i] ?? '')) return false
    }
    return true
  }

  private readonly dispatchNavigateFallback = (): void => {
    try {
      window.dispatchEvent(new Event('navigate'))
    } catch {
      // ignore
    }
  }
}

register('@hypercomb.social/Lineage', new Lineage())
console.log('[hypercomb] lineage: explorerDir memoized per fsRevision (2026-05-01)')
